import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, BookOpen, MessageSquare, Heart, Send, Shield, Users, TrendingUp, Layout } from 'lucide-react';
import { LeadMagnetPage } from '../components/mandate';
import { BlogPostTemplate } from '../components/mandate';
import { WelcomeEmailSequence } from '../components/mandate';
import { CaseStudyForm } from '../components/mandate';
import { TestimonialForm } from '../components/mandate';
import { RetentionDashboard } from '../components/mandate';
import { ReengagementCampaign } from '../components/mandate';
import { PartnershipAgreement } from '../components/mandate';
import { WhiteLabelGuidelines } from '../components/mandate';

interface TabConfig {
  id: string;
  label: string;
  icon: React.ElementType;
  group: string;
  component: React.ElementType;
}

const TABS: TabConfig[] = [
  { id: 'leadmagnet', label: 'Lead Magnet', icon: Download, group: 'Content Hub', component: LeadMagnetPage },
  { id: 'blog', label: 'Blog Posts', icon: BookOpen, group: 'Content Hub', component: BlogPostTemplate },
  { id: 'emails', label: 'Email Sequence', icon: MessageSquare, group: 'Content Hub', component: WelcomeEmailSequence },
  { id: 'casestudy', label: 'Case Studies', icon: Users, group: 'Partner Tools', component: CaseStudyForm },
  { id: 'testimonials', label: 'Testimonials', icon: MessageSquare, group: 'Partner Tools', component: TestimonialForm },
  { id: 'retention', label: 'Retention', icon: Heart, group: 'Retention', component: RetentionDashboard },
  { id: 'reengage', label: 'Re-engagement', icon: TrendingUp, group: 'Retention', component: ReengagementCampaign },
  { id: 'partnership', label: 'Partnership', icon: Shield, group: 'Legal', component: PartnershipAgreement },
  { id: 'whitelabel', label: 'White Label', icon: Layout, group: 'Legal', component: WhiteLabelGuidelines },
];

const GROUPS = ['Content Hub', 'Partner Tools', 'Retention', 'Legal'];

export const MandateView = () => {
  const [activeTab, setActiveTab] = useState('leadmagnet');

  const activeGroup = TABS.find(t => t.id === activeTab)?.group || 'Content Hub';

  return (
    <div className="flex flex-col h-full p-6 bg-slate-50 space-y-6 overflow-y-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">Content & Partner Mandate</h1>
          <p className="text-sm text-slate-500 mt-1">Manage marketing content, partner tools, retention, and legal agreements</p>
        </div>
        <div className="hidden md:flex items-center gap-1 bg-white rounded-xl p-1 border border-slate-200">
          {GROUPS.map(group => (
            <button
              key={group}
              onClick={() => {
                const firstTab = TABS.find(t => t.group === group);
                if (firstTab) setActiveTab(firstTab.id);
              }}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                activeGroup === group
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              {group}
            </button>
          ))}
        </div>
      </div>

      <div className="md:hidden overflow-x-auto pb-2">
        <div className="flex gap-2 min-w-max">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-white text-slate-500 hover:text-slate-700 border border-slate-200'
              }`}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="md:hidden border-b border-slate-200">
          <div className="flex gap-2 overflow-x-auto pb-2 px-4">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                <tab.icon size={14} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {TABS.filter(t => t.id === activeTab).map(tab => (
            <motion.div
              key={tab.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="p-6"
            >
              <React.Fragment key={tab.id}>
                {React.createElement(tab.component)}
              </React.Fragment>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default MandateView;